<!-- Header Email Sidebar Panel -->
<div class="email-sidebar-panel" [class.open]="isOpen">
  <!-- Header -->
  <div class="email-sidebar-header">
    <div class="header-top">
      <div class="header-title">
        <mat-icon class="title-icon">email</mat-icon>
        <span>Email</span>
      </div>
      <div class="header-actions">
        <button class="action-btn" (click)="toggleSearchBar()" title="Search">
          <mat-icon>search</mat-icon>
        </button>
        <button class="action-btn" (click)="refreshEmails()" title="Refresh" [class.spinning]="isLoading">
          <mat-icon>refresh</mat-icon>
        </button>
        <button class="action-btn compose-btn" (click)="composeNewEmail()" title="Compose">
          <mat-icon>edit</mat-icon>
        </button>
        <button class="action-btn close-btn" (click)="closeSidebar()" title="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Account Selector -->
    @if (userAccounts.length > 0) {
      <div class="account-selector-container">
        <button class="account-selector-trigger" (click)="toggleAccountSelector(); $event.stopPropagation()">
          <div class="current-account">
            <div class="account-avatar">
              <span class="e-avatar e-avatar-circle avatar-initials"
                [style.backgroundColor]="getAccountAvatarColor(activeAccount?.email)">
                {{ getAccountInitials(activeAccount?.email) }}
              </span>
            </div>
            <div class="account-info">
              <span class="account-name">{{ activeAccount?.name || 'Select Account' }}</span>
              <span class="account-email">{{ activeAccount?.email || '' }}</span>
            </div>
            <mat-icon class="dropdown-icon">{{ showAccountSelector ? 'expand_less' : 'expand_more' }}</mat-icon>
          </div>
        </button>
        <!-- Account Dropdown -->
        @if (showAccountSelector) {
          <div class="account-selector-dropdown" (click)="$event.stopPropagation()">
            <div class="dropdown-header">Switch Account</div>
            <div class="account-list">
              @for (account of userAccounts; track account) {
                <button class="account-option"
                  [class.active]="account.id === activeAccount?.id"
                  (click)="selectAccount(account); $event.stopPropagation()">
                  <div class="account-avatar">
                    <span class="e-avatar e-avatar-circle avatar-initials"
                      [style.backgroundColor]="getAccountAvatarColor(account.email)">
                      {{ getAccountInitials(account.email) }}
                    </span>
                  </div>
                  <div class="account-details">
                    <span class="account-name">{{ account.name }}</span>
                    <span class="account-email">{{ account.email }}</span>
                  </div>
                  @if (account.id === activeAccount?.id) {
                    <mat-icon class="check-icon">check</mat-icon>
                  }
                </button>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Folder Navigation -->
    @if (accountFolders.length > 0) {
      <div class="folder-navigation">
        <div class="folder-header">
          <span class="folder-title">Folders</span>
          <button class="folder-toggle-btn" (click)="toggleFolderTree(); $event.stopPropagation()">
            <mat-icon>{{ showFolderTree ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </div>
        @if (showFolderTree) {
          <div class="folder-tree">
            <!-- Root Level Folders -->
            @for (folder of rootFolders; track folder) {
              <div class="folder-item"
                [class.active]="activeFolder?.ID === folder.ID"
                [class.has-children]="folder.HasChild"
                (click)="selectFolder(folder, $event)">
                <!-- Expand/Collapse Arrow -->
                @if (folder.HasChild) {
                  <button class="expand-btn"
                    (click)="toggleFolderExpand(folder, $event)">
                    <mat-icon>{{ isFolderExpanded(folder) ? 'expand_more' : 'chevron_right' }}</mat-icon>
                  </button>
                }
                @if (!folder.HasChild) {
                  <span class="expand-spacer"></span>
                }
                <!-- Folder Icon & Name -->
                <mat-icon class="folder-icon">{{ getFolderIcon(folder) }}</mat-icon>
                <span class="folder-name">{{ folder.Name }}</span>
                <!-- Unread Count Badge -->
                @if (folder.Count && $any(folder.Count) > 0) {
                  <span class="folder-count">
                    {{ folder.Count }}
                  </span>
                }
              </div>
              <!-- Child Folders (Level 1) -->
              @if (folder.HasChild && isFolderExpanded(folder)) {
                <div class="folder-children">
                  @for (child of getChildFolders(folder.ID); track child) {
                    <div class="folder-item child-folder"
                      [class.active]="activeFolder?.ID === child.ID"
                      (click)="selectFolder(child, $event)">
                      <span class="expand-spacer"></span>
                      <mat-icon class="folder-icon">{{ getFolderIcon(child) }}</mat-icon>
                      <span class="folder-name">{{ child.Name }}</span>
                      @if (child.Count && $any(child.Count) > 0) {
                        <span class="folder-count">
                          {{ child.Count }}
                        </span>
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>
        }
        <!-- Clear Folder Filter -->
        @if (activeFolder) {
          <button class="clear-folder-btn"
            (click)="selectFolder(null, $event)">
            <mat-icon>clear</mat-icon>
            <span>Show All Emails</span>
          </button>
        }
      </div>
    }

    <!-- Search Bar (Collapsible) -->
    @if (showSearchBar) {
      <div class="search-bar">
        <mat-icon class="search-icon">search</mat-icon>
        <input
          type="text"
          class="search-input"
          placeholder="Search emails..."
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearchChange($event)"
          autofocus
          />
        @if (searchQuery) {
          <button class="clear-search-btn" (click)="clearSearch()">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
    }

    <!-- Tab Bar -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        [class.active]="activeTab === 'inbox'"
        (click)="setActiveTab('inbox')"
        >
        <mat-icon>inbox</mat-icon>
        <span>Inbox</span>
      </button>
      <button
        class="tab-btn"
        [class.active]="activeTab === 'unread'"
        (click)="setActiveTab('unread')"
        >
        <mat-icon>markunread</mat-icon>
        <span>Unread</span>
        @if (unreadCount > 0) {
          <span class="tab-badge">{{ unreadCount }}</span>
        }
      </button>
      <button
        class="tab-btn"
        [class.active]="activeTab === 'flagged'"
        (click)="setActiveTab('flagged')"
        >
        <mat-icon>flag</mat-icon>
        <span>Flagged</span>
        @if (flaggedCount > 0) {
          <span class="tab-badge flagged-badge">{{ flaggedCount }}</span>
        }
      </button>
    </div>
  </div>

  <!-- Content Area -->
  <div class="email-sidebar-content">
    <!-- Loading State -->
    @if (isLoading && emails.length === 0 && viewMode === 'list') {
      <div class="loading-state">
        <div class="spinner"></div>
        <span>Loading emails...</span>
      </div>
    }

    <!-- Reading Pane View -->
    @if (viewMode === 'reading' && selectedEmail) {
      <div class="reading-pane">
        <!-- Reading Pane Header -->
        <div class="reading-pane-header">
          <button class="back-btn" (click)="backToList($event)">
            <mat-icon>arrow_back</mat-icon>
            <span>Back</span>
          </button>
          <div class="reading-pane-actions">
            <button class="rp-action-btn" title="Reply" (click)="replyToEmail(selectedEmail, $event)">
              <mat-icon>reply</mat-icon>
            </button>
            <button class="rp-action-btn" title="Reply All" (click)="replyAllToEmail(selectedEmail, $event)">
              <mat-icon>reply_all</mat-icon>
            </button>
            <button class="rp-action-btn" title="Forward" (click)="forwardEmail(selectedEmail, $event)">
              <mat-icon>forward</mat-icon>
            </button>
            <button class="rp-action-btn" title="Delete" (click)="deleteEmail(selectedEmail, $event)">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>
        <!-- Email Content -->
        <div class="reading-pane-content">
          <!-- Subject line (shown once for the whole thread) -->
          <div class="email-subject-line">{{ selectedEmail.subject }}</div>
          <!-- Thread loading indicator -->
          @if (isLoadingThread) {
            <div class="thread-loading">
              <div class="spinner"></div>
              <span>Loading conversation...</span>
            </div>
          }
          <!-- Thread/Conversation View (multiple messages) -->
          @if (threadMessages.length > 0) {
            @for (msg of threadMessages; track msg; let isLast = $last) {
              <div class="thread-message"
                [class.expanded]="isLast" [class.collapsed]="!isLast">
                <!-- Message Header -->
                <div class="thread-message-header" (click)="!isLast ? null : null">
                  <div class="sender-avatar small" [style.background-color]="msg.senderAvatarColor">
                    <span class="avatar-text">{{ msg.senderInitials }}</span>
                  </div>
                  <div class="sender-details">
                    <span class="sender-name">{{ msg.senderName }}</span>
                    <span class="sender-email">&lt;{{ msg.senderEmail }}&gt;</span>
                  </div>
                  <span class="email-date">{{ msg.timeAgo }}</span>
                </div>
                <!-- CC Recipients -->
                @if (msg.ccRecipients && msg.ccRecipients.length > 0) {
                  <div class="email-recipients">
                    <span class="recipients-label">CC:</span>
                    <span class="recipients-list">{{ formatCcRecipients(msg.ccRecipients) }}</span>
                  </div>
                }
                <!-- Message Attachments -->
                @if (msg.attachments && msg.attachments.length > 0) {
                  <div class="email-attachments-section">
                    <div class="attachments-header">
                      <div class="attachments-info">
                        <mat-icon>attach_file</mat-icon>
                        <span class="attachments-count">{{ msg.attachments.length }} {{ msg.attachments.length === 1 ? 'Attachment' : 'Attachments' }}</span>
                      </div>
                    </div>
                    <div class="attachments-list">
                      @for (attachment of msg.attachments; track attachment) {
                        <div class="attachment-chip"
                          [title]="attachment.name + ' (' + formatFileSize(attachment.size) + ')'">
                          <div class="chip-icon" [ngClass]="'file-' + getFileExtension(attachment.name)">
                            <mat-icon>{{ getFileIcon(attachment.type) }}</mat-icon>
                          </div>
                          <span class="chip-name">{{ attachment.name }}</span>
                          <div class="chip-actions">
                            @if (isPreviewable(attachment)) {
                              <button class="chip-action-btn"
                                (click)="previewAttachment(attachment, $event)" title="Preview">
                                <mat-icon>visibility</mat-icon>
                              </button>
                            }
                            <button class="chip-action-btn" (click)="downloadAttachment(attachment, $event)" title="Download">
                              <mat-icon>download</mat-icon>
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
                <!-- Message Body -->
                <div class="email-body-content" [innerHTML]="msg.message || msg.preview"></div>
                <!-- Separator between messages -->
                @if (!isLast) {
                  <div class="thread-separator"></div>
                }
              </div>
            }
          }
          <!-- Single Email View (no thread or single message) -->
          @if (threadMessages.length === 0 && !isLoadingThread) {
            <div class="email-sender-row">
              <div class="sender-avatar" [style.background-color]="selectedEmail.senderAvatarColor">
                <span class="avatar-text">{{ selectedEmail.senderInitials }}</span>
              </div>
              <div class="sender-details">
                <span class="sender-name">{{ selectedEmail.senderName }}</span>
                <span class="sender-email">&lt;{{ selectedEmail.senderEmail }}&gt;</span>
              </div>
              <span class="email-date">{{ selectedEmail.timeAgo }}</span>
            </div>
            <!-- Recipients info -->
            @if (selectedEmail.ccRecipients && selectedEmail.ccRecipients.length > 0) {
              <div class="email-recipients">
                <span class="recipients-label">CC:</span>
                <span class="recipients-list">
                  {{ formatCcRecipients(selectedEmail.ccRecipients) }}
                </span>
              </div>
            }
            <!-- Attachments Section -->
            @if (selectedEmail.attachments && selectedEmail.attachments.length > 0) {
              <div class="email-attachments-section">
                <div class="attachments-header">
                  <div class="attachments-info">
                    <mat-icon>attach_file</mat-icon>
                    <span class="attachments-count">{{ selectedEmail.attachments.length }} {{ selectedEmail.attachments.length === 1 ? 'Attachment' : 'Attachments' }}</span>
                    <span class="attachments-size">({{ formatFileSize(getTotalAttachmentsSize()) }})</span>
                  </div>
                  <div class="attachments-actions">
                    <button class="download-all-btn" (click)="downloadAllAttachments($event)" title="Download all">
                      <mat-icon>download</mat-icon>
                      <span>Download All</span>
                    </button>
                  </div>
                </div>
                <div class="attachments-list">
                  @for (attachment of selectedEmail.attachments; track attachment) {
                    <div class="attachment-chip"
                      [title]="attachment.name + ' (' + formatFileSize(attachment.size) + ')'">
                      <div class="chip-icon" [ngClass]="'file-' + getFileExtension(attachment.name)">
                        <mat-icon>{{ getFileIcon(attachment.type) }}</mat-icon>
                      </div>
                      <span class="chip-name">{{ attachment.name }}</span>
                      <div class="chip-actions">
                        @if (isPreviewable(attachment)) {
                          <button class="chip-action-btn"
                            (click)="previewAttachment(attachment, $event)" title="Preview">
                            <mat-icon>visibility</mat-icon>
                          </button>
                        }
                        <button class="chip-action-btn" (click)="downloadAttachment(attachment, $event)" title="Download">
                          <mat-icon>download</mat-icon>
                        </button>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
            <div class="email-body-content" [innerHTML]="selectedEmail.message || selectedEmail.preview"></div>
          }
        </div>
        <!-- Reading Pane Footer -->
        <div class="reading-pane-footer">
          <button class="rp-footer-btn" (click)="replyToEmail(selectedEmail, $event)">
            <mat-icon>reply</mat-icon>
            <span>Reply</span>
          </button>
          <button class="rp-footer-btn" (click)="forwardEmail(selectedEmail, $event)">
            <mat-icon>forward</mat-icon>
            <span>Forward</span>
          </button>
        </div>
      </div>
    }

    <!-- Compose Pane View -->
    @if (viewMode === 'compose') {
      <div class="compose-pane">
        <!-- Compose Pane Header -->
        <div class="compose-pane-header">
          <button class="back-btn" (click)="backFromCompose($event)">
            <mat-icon>arrow_back</mat-icon>
            <span>Cancel</span>
          </button>
          <span class="compose-title">
            {{ composeMode === 'compose' ? 'New Email' :
            (composeMode === 'forward' ? 'Forward' : 'Reply') }}
          </span>
          <button class="priority-btn" [class.active]="priority === 'high'"
            (click)="togglePriority()" [title]="priority === 'high' ? 'Remove high priority' : 'Set high priority'">
            <mat-icon>{{ priority === 'high' ? 'priority_high' : 'flag' }}</mat-icon>
          </button>
        </div>
        <!-- Compose Form -->
        <div class="compose-pane-content">
          <!-- From Field -->
          @if (activeAccount) {
            <div class="compose-from-field">
              <label>From:</label>
              <div class="from-account">
                <div class="account-avatar small">
                  <span class="e-avatar e-avatar-circle avatar-initials"
                    [style.backgroundColor]="getAccountAvatarColor(activeAccount.email)">
                    {{ getAccountInitials(activeAccount.email) }}
                  </span>
                </div>
                <span class="from-email">{{ activeAccount.email }}</span>
                <button class="change-from-btn" (click)="toggleAccountSelector(); $event.stopPropagation()" title="Change account">
                  <mat-icon>swap_horiz</mat-icon>
                </button>
              </div>
            </div>
          }
          <!-- To Field -->
          <div class="compose-field to-field">
            <div class="field-row">
              <label>To:</label>
              <input type="text" [(ngModel)]="composeForm.to" placeholder="recipient@email.com" />
              <div class="cc-bcc-toggles">
                <button class="toggle-btn" [class.active]="showCC" (click)="toggleCC()">Cc</button>
                <button class="toggle-btn" [class.active]="showBCC" (click)="toggleBCC()">Bcc</button>
              </div>
            </div>
          </div>
          <!-- CC Field -->
          @if (showCC) {
            <div class="compose-field">
              <div class="field-row">
                <label>Cc:</label>
                <input type="text" [(ngModel)]="composeForm.cc" placeholder="cc@email.com" />
              </div>
            </div>
          }
          <!-- BCC Field -->
          @if (showBCC) {
            <div class="compose-field">
              <div class="field-row">
                <label>Bcc:</label>
                <input type="text" [(ngModel)]="composeForm.bcc" placeholder="bcc@email.com" />
              </div>
            </div>
          }
          <!-- Subject Field -->
          <div class="compose-field subject-field">
            <div class="field-row">
              <label>Subject:</label>
              <input type="text" [(ngModel)]="composeForm.subject" placeholder="Subject" />
            </div>
          </div>
          <!-- Body -->
          <div class="compose-body">
            <textarea [(ngModel)]="composeForm.body" placeholder="Write your message..."></textarea>
          </div>
          <!-- Attachments Section -->
          @if (attachments.length > 0) {
            <div class="attachments-section">
              <div class="attachments-header">
                <mat-icon>attach_file</mat-icon>
                <span class="attachments-count">{{ attachments.length }} {{ attachments.length === 1 ? 'file' : 'files' }}</span>
                <span class="attachments-size">({{ formatFileSize(getTotalAttachmentSize()) }})</span>
              </div>
              <div class="attachments-list">
                @for (file of attachments; track file; let i = $index) {
                  <div class="attachment-item">
                    <mat-icon class="file-icon">{{ getFileIcon(file.type) }}</mat-icon>
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    <button class="remove-attachment-btn" (click)="removeAttachment(i)" title="Remove">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
        <!-- Linked Entities Display -->
        @if (hasLinkedEntities()) {
          <div class="linked-entities">
            @if (linkedJob) {
              <div class="entity-chip job-chip">
                <mat-icon>work</mat-icon>
                <span>{{ linkedJob.name }}</span>
                <button class="remove-chip" (click)="removeEntityLink('job')">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
            @if (linkedCustomer) {
              <div class="entity-chip customer-chip">
                <mat-icon>business</mat-icon>
                <span>{{ linkedCustomer.name }}</span>
                <button class="remove-chip" (click)="removeEntityLink('customer')">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
            @if (linkedTicket) {
              <div class="entity-chip ticket-chip">
                <mat-icon>confirmation_number</mat-icon>
                <span>{{ linkedTicket.code }} - {{ linkedTicket.name }}</span>
                <button class="remove-chip" (click)="removeEntityLink('ticket')">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }
        <!-- Selected Labels Display -->
        @if (selectedLabels.length > 0) {
          <div class="selected-labels">
            @for (label of selectedLabels; track label) {
              <div class="label-chip"
                [style.background-color]="label.color + '20'"
                [style.border-color]="label.color">
                <span class="label-dot" [style.background-color]="label.color"></span>
                <span>{{ label.name }}</span>
                <button class="remove-chip" (click)="removeLabel(label)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }
        <!-- Schedule Indicator -->
        @if (isScheduled) {
          <div class="schedule-indicator">
            <mat-icon>schedule</mat-icon>
            <span>Scheduled: {{ scheduledDate | date:'MMM d, y h:mm a' }}</span>
            <button class="clear-schedule-btn" (click)="clearSchedule()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
        <!-- Compose Footer - Enhanced -->
        <div class="compose-pane-footer enhanced">
          <!-- Primary Send Actions Row -->
          <div class="footer-primary">
            <div class="send-group">
              <button class="send-btn" [class.loading]="isSending" [disabled]="!canSend || isSending"
                (click)="sendEmail($event)">
                @if (!isSending) {
                  <mat-icon>send</mat-icon>
                }
                @if (isSending) {
                  <span class="spinner"></span>
                }
                <span>{{ isSending ? 'Sending...' : sendButtonText }}</span>
              </button>
              <button class="send-options-btn" [matMenuTriggerFor]="sendMenu" [disabled]="isSending">
                <mat-icon>arrow_drop_down</mat-icon>
              </button>
              <mat-menu #sendMenu="matMenu" class="send-options-menu">
                <button mat-menu-item (click)="openSchedulePicker()">
                  <mat-icon>schedule</mat-icon>
                  <span>Schedule send</span>
                </button>
                <button mat-menu-item (click)="saveDraft()" [disabled]="isSavingDraft">
                  <mat-icon>save</mat-icon>
                  <span>{{ isSavingDraft ? 'Saving...' : 'Save draft' }}</span>
                </button>
              </mat-menu>
            </div>
          </div>
          <!-- Formatting Toolbar Row -->
          <div class="footer-toolbar">
            <!-- Formatting Group -->
            <div class="toolbar-group formatting">
              <button class="toolbar-btn" (click)="formatBold()" [class.active]="activeFormats.bold" title="Bold (Ctrl+B)">
                <mat-icon>format_bold</mat-icon>
              </button>
              <button class="toolbar-btn" (click)="formatItalic()" [class.active]="activeFormats.italic" title="Italic (Ctrl+I)">
                <mat-icon>format_italic</mat-icon>
              </button>
              <button class="toolbar-btn" (click)="formatUnderline()" [class.active]="activeFormats.underline" title="Underline (Ctrl+U)">
                <mat-icon>format_underlined</mat-icon>
              </button>
              <span class="toolbar-divider"></span>
              <button class="toolbar-btn" (click)="formatBullets()" title="Bulleted list">
                <mat-icon>format_list_bulleted</mat-icon>
              </button>
              <button class="toolbar-btn" (click)="formatNumbers()" title="Numbered list">
                <mat-icon>format_list_numbered</mat-icon>
              </button>
            </div>
            <!-- Actions Group -->
            <div class="toolbar-group actions">
              <input type="file" #fileInput multiple hidden (change)="onFilesSelected($event)"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar,.txt,.csv">
              <button class="toolbar-btn" (click)="triggerFileInput()" title="Attach files (Ctrl+Shift+A)">
                <mat-icon>attach_file</mat-icon>
                @if (attachments.length > 0) {
                  <span class="badge">{{ attachments.length }}</span>
                }
              </button>
              <button class="toolbar-btn" (click)="insertLink()" title="Insert link (Ctrl+K)">
                <mat-icon>link</mat-icon>
              </button>
              <button class="toolbar-btn" (click)="toggleEmojiPicker()" [class.active]="showEmojiPicker" title="Insert emoji">
                <mat-icon>mood</mat-icon>
              </button>
              <span class="toolbar-divider"></span>
              <button class="toolbar-btn" [matMenuTriggerFor]="entityMenu" title="Link to entity">
                <mat-icon>add_link</mat-icon>
              </button>
              <mat-menu #entityMenu="matMenu" class="entity-link-menu">
                <button mat-menu-item (click)="openEntityLinkDialog('job')">
                  <mat-icon>work</mat-icon>
                  <span>Link to Job</span>
                </button>
                <button mat-menu-item (click)="openEntityLinkDialog('customer')">
                  <mat-icon>business</mat-icon>
                  <span>Link to Customer</span>
                </button>
                <button mat-menu-item (click)="openEntityLinkDialog('ticket')">
                  <mat-icon>confirmation_number</mat-icon>
                  <span>Link to Ticket</span>
                </button>
              </mat-menu>
              <button class="toolbar-btn" (click)="openLabelPicker()" [class.active]="showLabelPicker" title="Add labels">
                <mat-icon>label</mat-icon>
                @if (selectedLabels.length > 0) {
                  <span class="badge">{{ selectedLabels.length }}</span>
                }
              </button>
              <button class="toolbar-btn" (click)="toggleSignatureSelector()" [class.active]="showSignatureSelector" title="Insert signature">
                <mat-icon>draw</mat-icon>
              </button>
              <span class="toolbar-divider"></span>
              <button class="toolbar-btn" [matMenuTriggerFor]="moreMenu" title="More options">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #moreMenu="matMenu" class="more-options-menu">
                <button mat-menu-item (click)="saveDraft()" [disabled]="isSavingDraft">
                  <mat-icon>save</mat-icon>
                  <span>{{ isSavingDraft ? 'Saving...' : 'Save draft' }}</span>
                </button>
                <div class="menu-divider"></div>
                <button mat-menu-item (click)="backFromCompose($event)" class="discard-menu-item">
                  <mat-icon>delete</mat-icon>
                  <span>Discard</span>
                </button>
              </mat-menu>
            </div>
          </div>
        </div>
        <!-- Emoji Picker Overlay -->
        @if (showEmojiPicker) {
          <div class="emoji-picker-overlay" (click)="$event.stopPropagation()">
            <div class="emoji-picker-header">
              <input type="text" class="emoji-search" placeholder="Search emoji..."
                [(ngModel)]="emojiSearchTerm" (input)="searchEmoji($event)">
              <button class="close-picker-btn" (click)="showEmojiPicker = false">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="emoji-grid">
              @for (emoji of filteredEmojis; track emoji) {
                <button class="emoji-btn"
                  (click)="insertEmoji(emoji)" [title]="emoji.name">
                  {{ emoji.char }}
                </button>
              }
            </div>
          </div>
        }
        <!-- Entity Link Panel Overlay -->
        @if (showEntityLinkPanel) {
          <div class="entity-link-panel-overlay" (click)="$event.stopPropagation()">
            <div class="panel-header">
              <span class="panel-title">
                {{ entityLinkType === 'job' ? 'Link to Job' :
                (entityLinkType === 'customer' ? 'Link to Customer' : 'Link to Ticket') }}
              </span>
              <button class="close-panel-btn" (click)="closeEntityLinkPanel()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="panel-content">
              <ng-select
              [items]="(entityLinkType === 'job' ? (jobOptions$ | async) :
                       (entityLinkType === 'customer' ? (customerOptions$ | async) : (ticketOptions$ | async))) ?? []"
              [typeahead]="entityLinkType === 'job' ? jobSearchInput$ :
                           (entityLinkType === 'customer' ? customerSearchInput$ : ticketSearchInput$)"
                bindLabel="name"
                [placeholder]="'Search ' + entityLinkType + '...'"
                [notFoundText]="'No ' + entityLinkType + 's found'"
                [loading]="false"
                [clearable]="true"
                (change)="selectEntity($event)"
                class="entity-search-select">
                <ng-template ng-option-tmp let-item="item">
                  <div class="entity-option">
                    <span class="entity-name">{{ item.job_name || item.company_name || item.name }}</span>
                    @if (item.ticket_code) {
                      <span class="entity-code">{{ item.ticket_code }}</span>
                    }
                  </div>
                </ng-template>
              </ng-select>
            </div>
          </div>
        }
        <!-- Label Picker Panel Overlay -->
        @if (showLabelPicker) {
          <div class="label-picker-panel-overlay" (click)="$event.stopPropagation()">
            <div class="panel-header">
              <span class="panel-title">Add Labels</span>
              <button class="close-panel-btn" (click)="closeLabelPicker()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="panel-content">
              <div class="label-options">
                @for (label of availableLabels; track label) {
                  <div class="label-option"
                    [class.selected]="isLabelSelected(label)"
                    (click)="toggleLabel(label)">
                    <span class="label-color" [style.background-color]="label.color"></span>
                    <span class="label-name">{{ label.name }}</span>
                    @if (isLabelSelected(label)) {
                      <mat-icon class="check-icon">check</mat-icon>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
        <!-- Signature Picker Panel Overlay -->
        @if (showSignatureSelector) {
          <div class="signature-picker-overlay" (click)="$event.stopPropagation()">
            <div class="panel-header">
              <span class="panel-title">Select Signature</span>
              <button class="close-panel-btn" (click)="showSignatureSelector = false">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="panel-content">
              <div class="signature-options">
                <div class="signature-option" [class.selected]="currentSignature === null"
                  (click)="onSignatureSelected(null)">
                  <mat-icon>block</mat-icon>
                  <span>No signature</span>
                </div>
                @for (sig of availableSignatures; track sig) {
                  <div class="signature-option"
                    [class.selected]="currentSignature?.id === sig.id"
                    (click)="onSignatureSelected(sig)">
                    <mat-icon>draw</mat-icon>
                    <span>{{ sig.name }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        }
        <!-- Schedule Picker Panel Overlay -->
        @if (showSchedulePicker) {
          <div class="schedule-picker-overlay" (click)="$event.stopPropagation()">
            <div class="panel-header">
              <span class="panel-title">Schedule Send</span>
              <button class="close-panel-btn" (click)="closeSchedulePicker()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="panel-content">
              <!-- Quick Options -->
              <div class="schedule-quick-options">
                <button class="quick-option" (click)="scheduleQuick('tomorrow_morning')">
                  <mat-icon>wb_sunny</mat-icon>
                  <span>Tomorrow morning</span>
                  <small>8:00 AM</small>
                </button>
                <button class="quick-option" (click)="scheduleQuick('tomorrow_afternoon')">
                  <mat-icon>wb_twilight</mat-icon>
                  <span>Tomorrow afternoon</span>
                  <small>1:00 PM</small>
                </button>
                <button class="quick-option" (click)="scheduleQuick('monday_morning')">
                  <mat-icon>calendar_today</mat-icon>
                  <span>Monday morning</span>
                  <small>8:00 AM</small>
                </button>
              </div>
              <div class="schedule-divider">
                <span>or pick a custom time</span>
              </div>
              <!-- Custom Date/Time -->
              <div class="schedule-custom">
                <div class="schedule-field">
                  <label>Date</label>
                  <input type="date" [(ngModel)]="scheduledDate" [min]="minScheduleDate | date:'yyyy-MM-dd'">
                </div>
                <div class="schedule-field">
                  <label>Time</label>
                  <input type="time" [(ngModel)]="scheduledTime">
                </div>
              </div>
              <div class="schedule-actions">
                <button class="schedule-confirm-btn" (click)="confirmSchedule()">
                  <mat-icon>schedule</mat-icon>
                  <span>Schedule</span>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Email List (Inbox/Unread/Flagged tabs) -->
    @if (viewMode === 'list' && activeTab !== 'notifications') {
      <!-- Quick Actions Bar -->
      @if (emails.length > 0 && activeTab === 'unread') {
        <div class="quick-actions-bar">
          <button class="quick-action-btn" (click)="markAllAsRead()">
            <mat-icon>done_all</mat-icon>
            <span>Mark all as read</span>
          </button>
        </div>
      }
      <!-- Empty State -->
      @if (!isLoading && emails.length === 0) {
        <div class="empty-state">
          <mat-icon class="empty-icon">
            {{ activeTab === 'inbox' ? 'inbox' : (activeTab === 'unread' ? 'mark_email_read' : 'flag') }}
          </mat-icon>
          <span class="empty-title">
            {{ activeTab === 'inbox' ? 'No emails' :
            (activeTab === 'unread' ? 'All caught up!' : 'No flagged emails') }}
          </span>
          <span class="empty-subtitle">
            {{ activeTab === 'inbox' ? 'Your inbox is empty' :
            (activeTab === 'unread' ? 'No unread emails' : 'Flag emails to see them here') }}
          </span>
        </div>
      }
      <!-- Email Items -->
      @if (emails.length > 0) {
        <div class="email-list">
          @for (email of emails; track trackByEmailId($index, email)) {
            <div
              class="email-item"
              [class.unread]="!email.isRead"
              [class.flagged]="email.isFlagged"
              [class.hovered]="hoveredEmailId === email.id"
              (click)="onEmailItemClick(email, $event)"
              (mouseenter)="hoveredEmailId = email.id"
              (mouseleave)="hoveredEmailId = null"
              >
              <!-- Avatar -->
              <div class="email-avatar" [style.background-color]="email.senderAvatarColor">
                <span class="avatar-text">{{ email.senderInitials }}</span>
                @if (email.priority === 'high') {
                  <span class="priority-indicator" title="High Priority"></span>
                }
              </div>
              <!-- Email Content -->
              <div class="email-content">
                <div class="email-header-row">
                  <span class="sender-name" [class.bold]="!email.isRead">{{ email.senderName }}</span>
                  <span class="email-time">{{ email.timeAgo }}</span>
                </div>
                <div class="email-subject" [class.bold]="!email.isRead">{{ email.subject }}</div>
                <div class="email-preview">{{ email.preview }}</div>
                <!-- Indicators Row -->
                <div class="email-indicators">
                  @if ((email.threadMessageCount || 0) > 1) {
                    <span class="thread-count" title="Conversation">{{ email.threadMessageCount }}</span>
                  }
                  @if (email.hasAttachment) {
                    <mat-icon class="indicator-icon attachment" title="Has attachment">attach_file</mat-icon>
                  }
                  @if (email.isFlagged) {
                    <mat-icon class="indicator-icon flagged" title="Flagged">flag</mat-icon>
                  }
                </div>
              </div>
              <!-- Quick Actions (visible on hover) -->
              <div class="email-actions" [class.visible]="hoveredEmailId === email.id">
                <button
                  class="email-action-btn"
                  [title]="email.isRead ? 'Mark as unread' : 'Mark as read'"
                  (click)="toggleRead(email, $event)"
                  >
                  <mat-icon>{{ email.isRead ? 'markunread' : 'drafts' }}</mat-icon>
                </button>
                <button
                  class="email-action-btn"
                  [class.flagged]="email.isFlagged"
                  [title]="email.isFlagged ? 'Remove flag' : 'Flag'"
                  (click)="toggleFlag(email, $event)"
                  >
                  <mat-icon>{{ email.isFlagged ? 'flag' : 'outlined_flag' }}</mat-icon>
                </button>
                <button
                  class="email-action-btn delete-btn"
                  title="Delete"
                  (click)="deleteEmail(email, $event)"
                  >
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </div>
          }
        </div>
      }
    }

  </div>

  <!-- Footer (only show in list mode) -->
  @if (viewMode === 'list') {
    <div class="email-sidebar-footer">
      <button class="footer-btn primary" (click)="navigateToInbox()">
        <mat-icon>open_in_new</mat-icon>
        <span>Open Email</span>
      </button>
      <button class="footer-btn" (click)="composeNewEmail(); $event.stopPropagation()">
        <mat-icon>create</mat-icon>
        <span>Compose</span>
      </button>
    </div>
  }
</div>

<!-- Backdrop (for mobile) -->
<div class="email-sidebar-backdrop" [class.visible]="isOpen" (click)="closeSidebar()"></div>
